import { Orbit } from 'lucide-react';
import React, { useMemo, useRef } from 'react';
import {
    CartesianGrid,
    Cell,
    LabelList,
    ReferenceArea,
    ReferenceLine,
    ResponsiveContainer,
    Scatter,
    ScatterChart,
    Tooltip,
    XAxis,
    YAxis,
    ZAxis,
} from 'recharts';
import type { TooltipProps } from 'recharts';

import type { SectorRRGPoint } from '../types';
import { cn, formatNum, quadrantTheme } from '../utils/formatters';
import { ChartDownloadMenu } from '../../../../shared/ui';
import { useChartExport } from '../../../../shared/hooks/useChartExport';
import { buildChartFilename } from '../../../../shared/utils/chartFilename';

interface Props {
    data: SectorRRGPoint[] | undefined;
}

// --- Custom tooltip: shows the sector's JdK coordinates + quadrant ------
const RrgTooltip = ({ active, payload }: TooltipProps<number, string>) => {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload as SectorRRGPoint;
    const theme = quadrantTheme(p.rrg_quadrant);
    return (
        <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border border-slate-200 dark:border-slate-700 p-3 rounded-xl shadow-xl text-sm min-w-[210px] z-50">
            <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 pb-2 mb-2">
                <span className="font-bold text-slate-900 dark:text-slate-100">
                    {p.ticker}
                    <span className="ml-2 text-xs font-medium text-slate-500">{p.name}</span>
                </span>
                <span
                    className={cn(
                        'text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border',
                        theme.badge,
                    )}
                >
                    {p.rrg_quadrant}
                </span>
            </div>
            <div className="space-y-1.5 text-xs">
                <Row label="RS-Ratio" value={formatNum(p.rs_ratio)} />
                <Row label="RS-Momentum" value={formatNum(p.rs_momentum)} />
            </div>
        </div>
    );
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div className="flex justify-between items-center gap-4">
        <span className="font-medium text-slate-500 dark:text-slate-400">{label}</span>
        <span className="font-mono font-bold text-slate-900 dark:text-white">{value}</span>
    </div>
);

// Renders the ticker symbol next to each scatter dot.
const TickerLabel = (props: any) => {
    const { x, y, value } = props;
    if (x == null || y == null) return null;
    return (
        <text
            x={x + 8}
            y={y + 4}
            fontSize={11}
            fontWeight={700}
            className="fill-slate-600 dark:fill-slate-300"
        >
            {value}
        </text>
    );
};

export const SectorRRGChart: React.FC<Props> = ({ data }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { downloadPng, downloadJpeg } = useChartExport(containerRef);

    // Compute symmetric axis bounds around the 100 pivot so the four quadrants
    // are visually balanced regardless of how tight the cross-section is.
    const { domainX, domainY } = useMemo(() => {
        if (!data || data.length === 0) {
            return { domainX: [90, 110], domainY: [90, 110] };
        }
        const pad = 2;
        const rs = data.map((d) => d.rs_ratio);
        const mo = data.map((d) => d.rs_momentum);
        const spanX = Math.max(
            ...rs.map((v) => Math.abs(v - 100)),
            5,
        );
        const spanY = Math.max(
            ...mo.map((v) => Math.abs(v - 100)),
            5,
        );
        return {
            domainX: [100 - spanX - pad, 100 + spanX + pad],
            domainY: [100 - spanY - pad, 100 + spanY + pad],
        };
    }, [data]);

    if (!data || data.length === 0) {
        return (
            <div className="flex h-full min-h-[460px] items-center justify-center text-slate-400 text-sm font-medium border border-dashed border-slate-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900">
                No RRG data available.
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm p-5 md:p-6 flex flex-col h-full min-h-[460px]">
            {/* Header */}
            <div className="flex items-center gap-3 mb-5 border-b border-slate-100 dark:border-slate-800 pb-4">
                <div className="p-2 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-lg">
                    <Orbit className="w-5 h-5" />
                </div>
                <div>
                    <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 tracking-tight">
                        Relative Rotation Graph
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        Relative Strength Ratio vs Relative Strength Momentum, benchmarked to S&P 500 Index.
                    </p>
                </div>
                <div className="ml-auto">
                    <ChartDownloadMenu
                        label="Relative Rotation Graph chart"
                        onPng={() => downloadPng({ filename: buildChartFilename('sector-rrg') })}
                        onJpeg={() => downloadJpeg({ filename: buildChartFilename('sector-rrg') })}
                    />
                </div>
            </div>

            {/* Chart */}
            <div ref={containerRef} className="flex-1 w-full relative min-h-[360px]">
                <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 0 }}>
                        {/* Quadrant background shading */}
                        <ReferenceArea
                            x1={100}
                            x2={domainX[1]}
                            y1={100}
                            y2={domainY[1]}
                            fill="#10b981"
                            fillOpacity={0.06}
                        />
                        <ReferenceArea
                            x1={100}
                            x2={domainX[1]}
                            y1={domainY[0]}
                            y2={100}
                            fill="#f59e0b"
                            fillOpacity={0.06}
                        />
                        <ReferenceArea
                            x1={domainX[0]}
                            x2={100}
                            y1={domainY[0]}
                            y2={100}
                            fill="#f43f5e"
                            fillOpacity={0.06}
                        />
                        <ReferenceArea
                            x1={domainX[0]}
                            x2={100}
                            y1={100}
                            y2={domainY[1]}
                            fill="#0ea5e9"
                            fillOpacity={0.06}
                        />

                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="currentColor"
                            className="text-slate-200 dark:text-slate-700/50"
                        />

                        <XAxis
                            type="number"
                            dataKey="rs_ratio"
                            name="RS-Ratio"
                            domain={domainX}
                            tick={{ fontSize: 11, fontWeight: 500 }}
                            tickFormatter={(v) => v.toFixed(0)}
                            axisLine={false}
                            tickLine={false}
                            stroke="currentColor"
                            className="text-slate-400 dark:text-slate-500"
                            label={{
                                value: 'RS-Ratio (Relative Strength)',
                                position: 'insideBottom',
                                offset: -10,
                                fontSize: 11,
                                fill: 'currentColor',
                            }}
                        />
                        <YAxis
                            type="number"
                            dataKey="rs_momentum"
                            name="RS-Momentum"
                            domain={domainY}
                            tick={{ fontSize: 11, fontWeight: 500 }}
                            tickFormatter={(v) => v.toFixed(0)}
                            axisLine={false}
                            tickLine={false}
                            stroke="currentColor"
                            className="text-slate-400 dark:text-slate-500"
                            label={{
                                value: 'RS-Momentum',
                                angle: -90,
                                position: 'insideLeft',
                                fontSize: 11,
                                fill: 'currentColor',
                            }}
                        />
                        <ZAxis range={[140, 140]} />

                        {/* The 100/100 pivot cross */}
                        <ReferenceLine x={100} stroke="#94a3b8" strokeDasharray="4 4" />
                        <ReferenceLine y={100} stroke="#94a3b8" strokeDasharray="4 4" />

                        <Tooltip content={<RrgTooltip />} cursor={{ strokeDasharray: '3 3' }} />

                        <Scatter data={data} isAnimationActive={false}>
                            {data.map((d) => (
                                <Cell key={d.ticker} fill={quadrantTheme(d.rrg_quadrant).hex} />
                            ))}
                            <LabelList dataKey="ticker" content={<TickerLabel />} />
                        </Scatter>
                    </ScatterChart>
                </ResponsiveContainer>
            </div>

            {/* Quadrant legend */}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <LegendPill hex="#10b981" label="Leading" hint="Strong & rising" />
                <LegendPill hex="#f59e0b" label="Weakening" hint="Strong, fading" />
                <LegendPill hex="#f43f5e" label="Lagging" hint="Weak & falling" />
                <LegendPill hex="#0ea5e9" label="Improving" hint="Weak, recovering" />
            </div>
        </div>
    );
};

const LegendPill: React.FC<{ hex: string; label: string; hint: string }> = ({
    hex,
    label,
    hint,
}) => (
    <div className="flex items-center gap-2">
        <span
            className="w-3 h-3 rounded-full shrink-0"
            style={{ backgroundColor: hex }}
        />
        <span className="font-bold text-slate-700 dark:text-slate-300">{label}</span>
        <span className="text-slate-400 dark:text-slate-500 hidden sm:inline">· {hint}</span>
    </div>
);

export default SectorRRGChart;
